import React from 'react';
import { Users, CheckCircle, Download, User, Calendar } from 'lucide-react';
import Button from '../../../components/ui/Button';
import EmptyState from '../../../components/EmptyState';

const ActiveOnboardingCases = ({ 
  onboardingCases, 
  onCreateCase, 
  onExportChecklist,
  onUpdateTaskStatus 
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-blue-500';
    if (percentage >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString();
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (!onboardingCases || onboardingCases?.length === 0) {
    return (
      <EmptyState
        icon={<Users />}
        title="No active onboarding cases"
        description="Start onboarding new hires to see their progress and track completion here. Onboarding cases help ensure all new employees receive proper orientation."
        primaryAction={{
          label: 'Start Onboarding',
          icon: Users,
          onClick: onCreateCase
        }}
        secondaryAction={{
          label: 'Create Template',
          icon: CheckCircle,
          onClick: () => console.log('Create template')
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Active Onboarding Cases ({onboardingCases?.length})
        </h3>
        <Button onClick={onCreateCase}>
          <Users className="h-4 w-4 mr-2" />
          Start New Onboarding
        </Button>
      </div>
      <div className="space-y-4">
        {onboardingCases?.map((onboardingCase) => (
          <div key={onboardingCase?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Case Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-lg mr-4">
                  {onboardingCase?.employeeName?.charAt(0) || 'N'}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    {onboardingCase?.employeeName || 'New Employee'}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {onboardingCase?.position || 'Position TBD'}
                  </p>
                  <p className="text-sm text-gray-500">
                    Started {formatDate(onboardingCase?.startDate)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(onboardingCase?.status)}`}>
                  {onboardingCase?.status || 'pending'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onExportChecklist(onboardingCase?.id)}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Overall Progress
                </span>
                <span className="text-sm text-gray-600">
                  {onboardingCase?.completionPercentage || 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getProgressColor(onboardingCase?.completionPercentage || 0)}`}
                  style={{ width: `${onboardingCase?.completionPercentage || 0}%` }}
                ></div>
              </div>
            </div>

            {/* Case Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="flex items-center text-sm text-gray-600">
                <User className="h-4 w-4 mr-2 text-gray-400" />
                <div>
                  <div className="font-medium">Coordinator</div>
                  <div>{onboardingCase?.coordinatorName || 'Unassigned'}</div>
                </div>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                <div>
                  <div className="font-medium">Expected Completion</div>
                  <div className={isOverdue(onboardingCase?.expectedCompletion) ? 'text-red-600' : ''}>
                    {formatDate(onboardingCase?.expectedCompletion) || 'Not set'}
                  </div>
                </div>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <CheckCircle className="h-4 w-4 mr-2 text-gray-400" />
                <div>
                  <div className="font-medium">Tasks</div>
                  <div>
                    {onboardingCase?.completedTasks || 0} of {onboardingCase?.totalTasks || 0} complete
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Tasks */}
            {onboardingCase?.recentTasks && onboardingCase?.recentTasks?.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-900 mb-2">Recent Tasks</h5>
                <div className="space-y-2">
                  {onboardingCase?.recentTasks?.slice(0, 3)?.map((task) => (
                    <div key={task?.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={task?.isCompleted || false}
                          onChange={(e) => onUpdateTaskStatus(task?.id, e?.target?.checked)}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300"
                        />
                        <span className={`ml-3 text-sm ${task?.isCompleted ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {task?.title || 'Untitled Task'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">
                          {task?.assigneeType || 'unassigned'}
                        </span>
                        {task?.dueDate && isOverdue(task?.dueDate) && !task?.isCompleted && (
                          <span className="text-xs text-red-600 font-medium">
                            Overdue
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {onboardingCase?.recentTasks?.length > 3 && (
                    <div className="text-sm text-gray-500 text-center py-1">
                      +{onboardingCase?.recentTasks?.length - 3} more tasks
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <Button variant="ghost" size="sm">
                View Details
              </Button>
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm">
                  Send Reminder
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onExportChecklist(onboardingCase?.id)}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActiveOnboardingCases;